import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

/**
 * Rótulo é exibido numa pill estreita (ver `SidebarLinkCard`) — mais curto
 * que o teto de 60 usado em títulos de destaque da capa (`CharCountWidget`).
 */
const LABEL_RECOMMENDED_LENGTH = 40;

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_conteudos_relacionados_block',
    defaultMessage: 'Conteúdos Relacionados',
  },
  items: {
    id: 'mpf_conteudos_relacionados_items',
    defaultMessage: 'Links',
  },
  item: {
    id: 'mpf_conteudos_relacionados_item',
    defaultMessage: 'Link',
  },
  label: {
    id: 'mpf_conteudos_relacionados_label',
    defaultMessage: 'Rótulo',
  },
  labelHelp: {
    id: 'mpf_conteudos_relacionados_label_help',
    defaultMessage:
      'Texto exibido no link. Prefira um rótulo específico que diga para onde ele leva — evite termos genéricos como "clique aqui", "saiba mais" ou "ver notícia".',
  },
  labelPlaceholder: {
    id: 'mpf_conteudos_relacionados_label_placeholder',
    defaultMessage: 'Ex.: Consulta processual no MPU',
  },
  href: {
    id: 'mpf_conteudos_relacionados_href',
    defaultMessage: 'Link',
  },
  hrefHelp: {
    id: 'mpf_conteudos_relacionados_href_help',
    defaultMessage:
      'Destino do link — pode ser uma página do próprio Portal MPF ou um site externo (ex.: MPU, Portal da Transparência). Ao escolher uma página interna, o rótulo acima é preenchido com o título dela; se você já tiver digitado um rótulo, ele não é sobrescrito.',
  },
  external: {
    id: 'mpf_conteudos_relacionados_external',
    defaultMessage: 'Abrir em nova aba',
  },
  externalHelp: {
    id: 'mpf_conteudos_relacionados_external_help',
    defaultMessage:
      'Controla só se o link abre numa aba nova — não interfere no destino escolhido acima, que pode ser interno ou externo independente desta opção.',
  },
});

/**
 * Diferente do bloco "Serviços em Destaque" (RF05, seleção travada num
 * catálogo): aqui o rótulo é texto livre do editor e o destino aceita
 * qualquer página interna do Portal MPF OU uma URL externa — é a versão
 * "destravada" do mesmo card, para links de apoio que não fazem parte do
 * catálogo oficial de serviços (ex.: nota técnica no MPU, Portal da
 * Transparência).
 */
const conteudoRelacionadoItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['label', 'href', 'external'],
    },
  ],
  properties: {
    label: {
      title: intl.formatMessage(messages.label),
      description: intl.formatMessage(messages.labelHelp),
      placeholder: intl.formatMessage(messages.labelPlaceholder),
      widget: 'charCount',
      recommendedLength: LABEL_RECOMMENDED_LENGTH,
    },
    href: {
      title: intl.formatMessage(messages.href),
      description: intl.formatMessage(messages.hrefHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.external),
      description: intl.formatMessage(messages.externalHelp),
      type: 'boolean',
    },
  },
  required: ['label', 'href'],
});

/** Ver `AcoesRapidas/schema.ts` sobre `activeObject`/`setActiveObject`. */
export const mpfConteudosRelacionadosSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['items'],
    },
  ],
  properties: {
    items: {
      title: intl.formatMessage(messages.items),
      widget: 'object_list',
      schema: conteudoRelacionadoItemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfConteudosRelacionadosSchema;
